import { IScope } from 'angular';
import { EquipmentCheckStatus } from './status';
import { VideoSceneController } from 'go-modules/video-scene/video-scene.controller';
import { Destination } from 'go-modules/video-scene/destinations/destination';
import { VideoSceneOptions } from 'go-modules/video-scene/video-scene.options';
import { States } from 'go-modules/video-scene/state-emitter/state-emitter';
import { CameraSource, MicrophoneSource } from 'go-modules/video-scene/go-source';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { MediaSource } from '../../models/media';
import { pairwise, Subject, takeUntil } from 'rxjs';

interface Bindings {
	recorderOptions: VideoSceneOptions;
}

enum MicrophoneStatus {
	ACTIVE = 'active',
	FAILED = 'failed'
}

export class EquipmentCheckModalController implements Bindings, Partial<ng.ui.bootstrap.IModalScope> {
	public cameraCheckStatus: EquipmentCheckStatus;
	public microphoneCheckStatus: EquipmentCheckStatus;
	public internetConnectionStatus: EquipmentCheckStatus;
	public serverConnectionStatus: States;
	public cameraLoadingStatusMessage: string = 'modal-equipment-check_checking-camera';
	public microphoneLoadingStatusMessage: string = 'modal-equipment-check_checking-microphone';
	public connectionLoadingStatusMessage: string = 'modal-equipment-check_checking-connection';
	public cameraSuccessStatusMessage: string = '';
	public microphoneSuccessStatusMessage: string = '';
	public connectionSuccessStatusMessage: string = '';
	public cameraErrorStatusMessage: string = '';
	public microphoneErrorStatusMessage: string = '';
	public connectionErrorStatusMessage: string = '';
	public recorderOptions: VideoSceneOptions;
	public whitelabel: boolean;
	public goRecorder: VideoSceneController;
	public $close: (result?: any) => boolean;
	public $dismiss: (result?: any) => boolean;
	public ready: boolean = false;

	public group_id: number;
	public media_id: number;
	public activity;
	private environmentVarsService;
	private readonly componentDestroyed$$ = new Subject<void>();
	private internetStatus$ = new Subject<boolean>();

	/* @ngInject */
	constructor (
		private $scope: IScope,
		public helpUrls,
		private MediaModel,
		private $log: ng.ILogService
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();
		this.cameraCheckStatus = EquipmentCheckStatus.PENDING;
		this.microphoneCheckStatus = EquipmentCheckStatus.PENDING;
		this.internetConnectionStatus = EquipmentCheckStatus.PENDING;
		this.watchEquipmentStatusMessages();
	}

	/**
	 * Handle controller init life-cycle hook
	 */
	public $onInit () {
		if (!this.recorderOptions || !this.group_id) {
			throw new Error('Property binding `recorderOptions` is required');
		}
		this.whitelabel = this.environmentVarsService.get(EnvironmentVarsService.VAR.WHITELABEL) as boolean;

		this.MediaModel.model({
			group_id: this.group_id,
			source: MediaSource.OPENTOK,
			media_type: this.MediaModel.TYPE.VIDEO
		}).$save().then((media: any) => {
			this.media_id = media.media_id;
			return media.getStreamingConnectionInfo();
		}).then((info) => {
			this.recorderOptions[MediaSource.OPENTOK] = info;
			this.ready = true;
		}).catch((err) => {
			this.$log.error(err);
		});

		this.internetStatus$.pipe(
			takeUntil(this.componentDestroyed$$),
			pairwise()
		).subscribe(([IsPreviousConnectionGood, IsCurrentConnectionGood]) => {
			const goodConnection = IsPreviousConnectionGood && IsCurrentConnectionGood;

			this.$scope.$evalAsync(() => {
				this.internetConnectionStatus =  goodConnection ?
					EquipmentCheckStatus.SUCCESS : EquipmentCheckStatus.FAILURE;
			});
		});
	}

	public $onDestroy (): void {
		this.componentDestroyed$$.next();
		this.componentDestroyed$$.complete();
	}

	/**
	 * Handle recorder init event
	 */
	public onRecorderInit (recorder: VideoSceneController) {
		this.goRecorder = recorder;

		recorder.on(CameraSource.event, (cameraStatus: string) => {
			let status = -1;
			if (cameraStatus === MicrophoneStatus.ACTIVE) {
				status = 1;
			} else if (cameraStatus === MicrophoneStatus.FAILED) {
				status = 0;
			}

			this.$scope.$evalAsync(() => {
				if(status === 1) {
					this.cameraCheckStatus = EquipmentCheckStatus.SUCCESS;
				} else if(status === 0) {
					this.cameraCheckStatus = EquipmentCheckStatus.FAILURE;
				}
			});
		});

		recorder.on(MicrophoneSource.event, (microphoneStatus: string) => {
			let status = -1;
			if (microphoneStatus === MicrophoneStatus.ACTIVE) {
				status = 1;
			} else if (microphoneStatus === MicrophoneStatus.FAILED) {
				status = 0;
			}

			this.$scope.$evalAsync(() => {
				if(status === 1) {
					this.microphoneCheckStatus = EquipmentCheckStatus.SUCCESS;
				} else if(status === 0) {
					this.microphoneCheckStatus = EquipmentCheckStatus.FAILURE;
				}
			});
		});

		recorder.on(Destination.event, (connectionStatus: States) => {
			this.$scope.$evalAsync(() => {
				this.serverConnectionStatus = connectionStatus;
			});
		});
	}

	public onConnectionStatus (goodConnection: boolean) {
		this.internetStatus$.next(goodConnection);
	}

	public isConnectionStatusOk (): boolean {
		if (this.activity.isSingleAttempt()) {
			return true;
		}
		return this.internetConnectionStatus === EquipmentCheckStatus.SUCCESS;
	}

	/**
	 * Handle recorder init error event
	 */
	public onRecorderInitError () {
		this.cameraCheckStatus = EquipmentCheckStatus.FAILURE;
		this.microphoneCheckStatus = EquipmentCheckStatus.FAILURE;
		this.updateCameraStatusMessages(this.cameraCheckStatus);
		this.updateMicrophoneStatusMessages(this.microphoneCheckStatus);
	}

	public close () {
		this.$close();
	}

	/**
	 * Whether all the equipment checks have succeeded
	 */
	public isReady (): boolean {
		if (!this.goRecorder) {
			return false;
		}

		if (this.recorderOptions && !!this.recorderOptions.equipmentOnly) {
			return this.goRecorder.isEquipmentReady() && this.isConnectionStatusOk();
		}

		return this.goRecorder.isReady() && this.isConnectionStatusOk();
	}

	private watchEquipmentStatusMessages (): void {
		this.$scope.$watch(
			() => this.cameraCheckStatus,
			(status: EquipmentCheckStatus) => this.updateCameraStatusMessages(status)
		);
		this.$scope.$watch(
			() => this.microphoneCheckStatus,
			(status: EquipmentCheckStatus) => this.updateMicrophoneStatusMessages(status)
		);
		this.$scope.$watch(
			() => this.internetConnectionStatus,
			(status: EquipmentCheckStatus) => this.updateConnectionStatusMessages(status)
		);
	}

	private updateCameraStatusMessages (status: EquipmentCheckStatus): void {
		this.cameraLoadingStatusMessage = status === EquipmentCheckStatus.PENDING ?
			'modal-equipment-check_checking-camera' : '';
		this.cameraSuccessStatusMessage = status === EquipmentCheckStatus.SUCCESS ?
			'modal-equipment-check_camera-passed-announcement' : '';
		this.cameraErrorStatusMessage = status === EquipmentCheckStatus.FAILURE ?
			'modal-equipment-check_camera-failure-announcement' : '';
	}

	private updateMicrophoneStatusMessages (status: EquipmentCheckStatus): void {
		this.microphoneLoadingStatusMessage = status === EquipmentCheckStatus.PENDING ?
			'modal-equipment-check_checking-microphone' : '';
		this.microphoneSuccessStatusMessage = status === EquipmentCheckStatus.SUCCESS ?
			'modal-equipment-check_microphone-passed-announcement' : '';
		this.microphoneErrorStatusMessage = status === EquipmentCheckStatus.FAILURE ?
			'modal-equipment-check_microphone-failure-announcement' : '';
	}

	private updateConnectionStatusMessages (status: EquipmentCheckStatus): void {
		this.connectionLoadingStatusMessage = status === EquipmentCheckStatus.PENDING ?
			'modal-equipment-check_checking-connection' : '';
		this.connectionSuccessStatusMessage = status === EquipmentCheckStatus.SUCCESS ?
			'modal-equipment-check_connection-passed-announcement' : '';
		this.connectionErrorStatusMessage = status === EquipmentCheckStatus.FAILURE ?
			'modal-equipment-check_connection-failure-announcement' : '';
	}
}
